import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Tabs,
    Tab,
    Button,
    Chip,
    Avatar,
    LinearProgress,
    IconButton,
    Tooltip,
    Paper
} from '@mui/material';
import {
    EmojiEvents as TrophyIcon,
    Psychology as BrainIcon,
    SportsSoccer as SportsIcon,
    Palette as ArtIcon,
    Science as ScienceIcon,
    Computer as TechIcon,
    Group as LeadershipIcon,
    Add as AddIcon,
    TrendingUp as TrendingUpIcon,
    Assessment as AssessmentIcon,
    Star as StarIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import TalentIdentification from './TalentIdentification';
import OlympiadManagement from './OlympiadManagement';
import HolisticAssessment from './HolisticAssessment';
import StudentTalentProfile from './StudentTalentProfile';
import CompetitionTracker from './CompetitionTracker';
import AnalyticsDashboard from './AnalyticsDashboard';

const TalentOlympiadDashboard = () => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [dashboardStats, setDashboardStats] = useState({
        totalStudents: 0,
        olympiadParticipants: 0,
        talentIdentified: 0,
        achievements: 0
    });

    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        // Fetch dashboard statistics
        fetchDashboardStats();
    }, []);

    const fetchDashboardStats = async () => {
        // API call to fetch statistics
        // Placeholder data for now
        setDashboardStats({
            totalStudents: 1250,
            olympiadParticipants: 342,
            talentIdentified: 89,
            achievements: 156
        });
    };

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    const statCards = [
        {
            title: 'Total Students',
            value: dashboardStats.totalStudents,
            icon: <LeadershipIcon fontSize="large" />,
            color: '#3f51b5',
            trend: '+12%'
        },
        {
            title: 'Olympiad Participants',
            value: dashboardStats.olympiadParticipants,
            icon: <TrophyIcon fontSize="large" />,
            color: '#ff9800',
            trend: '+24%'
        },
        {
            title: 'Talents Identified',
            value: dashboardStats.talentIdentified,
            icon: <StarIcon fontSize="large" />,
            color: '#4caf50',
            trend: '+18%'
        },
        {
            title: 'Achievements',
            value: dashboardStats.achievements,
            icon: <TrendingUpIcon fontSize="large" />,
            color: '#e91e63',
            trend: '+31%'
        }
    ];

    const talentCategories = [
        { name: 'Academic', icon: <BrainIcon />, color: '#3f51b5', count: 234 },
        { name: 'Sports', icon: <SportsIcon />, color: '#ff9800', count: 156 },
        { name: 'Creative Arts', icon: <ArtIcon />, color: '#e91e63', count: 89 },
        { name: 'Science', icon: <ScienceIcon />, color: '#4caf50', count: 178 },
        { name: 'Technology', icon: <TechIcon />, color: '#9c27b0', count: 112 },
        { name: 'Leadership', icon: <LeadershipIcon />, color: '#00bcd4', count: 67 }
    ];

    const renderTabContent = () => {
        switch (selectedTab) {
            case 0:
                return <TalentIdentification />;
            case 1:
                return <OlympiadManagement />;
            case 2:
                return <CompetitionTracker />;
            case 3:
                return <HolisticAssessment />;
            case 4:
                return <StudentTalentProfile />;
            case 5:
                return <AnalyticsDashboard />;
            default:
                return <TalentIdentification />;
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>
                    Talent Tracking & Olympiad Management
                </Typography>
                <Typography variant="body1" sx={{ color: '#546e7a' }}>
                    NEP 2020 Compliant Holistic Student Development System
                </Typography>
            </Box>

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {statCards.map((stat, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card
                            sx={{
                                background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`,
                                color: 'white',
                                height: '100%',
                                transition: 'transform 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-5px)',
                                    boxShadow: 6
                                }
                            }}
                        >
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                            {stat.title}
                                        </Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                                            {stat.value}
                                        </Typography>
                                        <Chip
                                            label={stat.trend}
                                            size="small"
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.2)',
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>
                                    <Avatar
                                        sx={{
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            width: 56,
                                            height: 56
                                        }}
                                    >
                                        {stat.icon}
                                    </Avatar>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Talent Categories Overview */}
            <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    Talent Categories Distribution
                </Typography>
                <Grid container spacing={2}>
                    {talentCategories.map((category, index) => (
                        <Grid item xs={12} sm={6} md={2} key={index}>
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: '#f5f5f5',
                                    transition: 'all 0.3s',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: category.color,
                                        color: 'white',
                                        transform: 'scale(1.05)',
                                        '& .MuiSvgIcon-root': {
                                            color: 'white'
                                        }
                                    }
                                }}
                            >
                                <Box sx={{ color: category.color, mb: 1 }}>
                                    {category.icon}
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {category.name}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {category.count}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Main Navigation Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.95rem'
                        }
                    }}
                >
                    <Tab label="Talent Identification" icon={<BrainIcon />} iconPosition="start" />
                    <Tab label="Olympiad Management" icon={<TrophyIcon />} iconPosition="start" />
                    <Tab label="Competition Tracker" icon={<AssessmentIcon />} iconPosition="start" />
                    <Tab label="Holistic Assessment" icon={<StarIcon />} iconPosition="start" />
                    <Tab label="Student Profiles" icon={<LeadershipIcon />} iconPosition="start" />
                    <Tab label="Analytics & Reports" icon={<TrendingUpIcon />} iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            <Box>
                {renderTabContent()}
            </Box>
        </Box>
    );
};

export default TalentOlympiadDashboard;

